import { useEffect, useState, useSyncExternalStore } from "react";
import { cx } from "@mfa-aff/ui";

/**
 * Lines on selling and marketing whose wording and author are well documented
 * (Ziglar: Secrets of Closing the Sale; Sinek: Start with Why; Baer: Youtility;
 * Drucker: Management; Gates: Business @ the Speed of Thought; Carnegie: How to
 * Win Friends; Buffett: 2008 shareholder letter), translated into plain Persian.
 */
const QUOTES: { text: string; name: string; role: string }[] = [
    { text: "اگر به اندازهٔ کافی به دیگران کمک کنید تا به خواسته‌شان برسند، شما هم به هر چه از زندگی بخواهید می‌رسید.", name: "زیگ زیگلار", role: "نویسنده و استاد فروش" },
    { text: "مردم برای کاری که انجام می‌دهید پول نمی‌دهند؛ برای دلیلی پول می‌دهند که آن را انجام می‌دهید.", name: "سایمون سینک", role: "نویسندهٔ «از چرا شروع کنید»" },
    { text: "برای محصولتان دنبال مشتری نگردید؛ برای مشتری‌هایتان دنبال محصول بگردید.", name: "ست گودین", role: "نویسنده و بازاریاب" },
    { text: "بهترین بازاریابی آن است که اصلاً حس بازاریابی ندهد.", name: "تام فیشبرن", role: "کارتونیست دنیای بازاریابی" },
    { text: "محتوا آتش است و شبکه‌های اجتماعی بنزینِ آن.", name: "جِی بِیر", role: "مشاور بازاریابی و نویسنده" },
    { text: "هدف بازاریابی این است که مشتری را آن‌قدر خوب بشناسید که محصول درست اندازهٔ نیازش باشد و خودش فروش برود.", name: "پیتر دراکر", role: "پدر مدیریت نوین" },
    { text: "بهترین راهبرد بازاریابی تمام دوران فقط یک کلمه است: اهمیت دادن.", name: "گری وینرچاک", role: "کارآفرین و بازاریاب" },
    { text: "مشتری را قهرمان داستانتان کنید.", name: "اَن هندلی", role: "نویسندهٔ «همه می‌نویسند»" },
    { text: "ناراضی‌ترین مشتری‌هایتان بهترین معلم‌های شما هستند.", name: "بیل گیتس", role: "بنیان‌گذار مایکروسافت" },
    { text: "اگر به دیگران علاقه نشان دهید، در دو ماه بیشتر دوست پیدا می‌کنید تا اینکه دو سال تلاش کنید دیگران به شما علاقه‌مند شوند.", name: "دیل کارنگی", role: "نویسندهٔ «آیین دوست‌یابی»" },
    { text: "قیمت چیزی است که می‌پردازید؛ ارزش چیزی است که به دست می‌آورید.", name: "وارن بافت", role: "سرمایه‌گذار افسانه‌ای" },
];

const TYPE_MS = 38;
const HOLD_MS = 7000;
const FADE_MS = 400;

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const subscribeReducedMotion = (onChange: () => void) => {
    const mq = window.matchMedia(REDUCED_MOTION);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
};
const useReducedMotion = () =>
    useSyncExternalStore(
        subscribeReducedMotion,
        () => window.matchMedia(REDUCED_MOTION).matches,
        () => false,
    );

/** Partner top bar: one quote at a time, typed out, then the next. Reduced motion: shown whole, no rotation. */
export const QuoteOfTheMoment = () => {
    const reduced = useReducedMotion();
    const [i, setI] = useState(() => Math.floor(Math.random() * QUOTES.length));
    const [typed, setTyped] = useState(0);
    const [visible, setVisible] = useState(true);
    const q = QUOTES[i]!;
    // Code points, so a letter is never split; Persian letters join as they appear.
    const chars = Array.from(q.text);
    const done = reduced || typed >= chars.length;

    useEffect(() => {
        if (reduced) return;
        if (typed < chars.length) {
            const t = window.setTimeout(() => setTyped((n) => n + 1), TYPE_MS);
            return () => window.clearTimeout(t);
        }
        const t = window.setTimeout(() => {
            setVisible(false);
            window.setTimeout(() => {
                setI((n) => (n + 1) % QUOTES.length);
                setTyped(0);
                setVisible(true);
            }, FADE_MS);
        }, HOLD_MS);
        return () => window.clearTimeout(t);
    }, [reduced, typed, chars.length]);

    return (
        <figure className={cx("flex min-w-0 items-center gap-2.5 transition-opacity", visible ? "opacity-100" : "opacity-0")} style={{ transitionDuration: `${FADE_MS}ms` }}>
            <svg aria-hidden="true" viewBox="0 0 32 32" className="size-5 shrink-0 text-[#51B848]" fill="currentColor">
                <path d="M13 7c-4.9 1.4-8 5.3-8 10.6V25h8.4v-8.2H9.2c.2-3.4 2-5.7 5-6.8L13 7zm14 0c-4.9 1.4-8 5.3-8 10.6V25h8.4v-8.2h-4.2c.2-3.4 2-5.7 5-6.8L27 7z" />
            </svg>
            <blockquote aria-label={`${q.text} (${q.name})`} title={`${q.text} (${q.name}، ${q.role})`} className="min-w-0">
                <p aria-hidden="true" className="line-clamp-2 text-sm leading-5 font-medium text-secondary">
                    {reduced ? q.text : chars.slice(0, typed).join("")}
                    {!done && <span className="ms-0.5 inline-block h-[1.05em] w-0.5 translate-y-0.5 animate-pulse bg-[#51B848]" />}
                    <span className={cx("ms-2 text-tertiary transition-opacity duration-500", done ? "opacity-100" : "opacity-0")}>({q.name})</span>
                </p>
            </blockquote>
        </figure>
    );
};
